import React, { useState, useEffect } from 'react';
import { Container, Table, Button, Modal, Form, Alert, Spinner, Image } from 'react-bootstrap';
import { Plus, Edit, Trash2, Eye } from 'lucide-react';
import config from '../../config';

const FabricsManagePage = () => {
  const [fabrics, setFabrics] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingFabric, setEditingFabric] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    main_image: null
  });
  const [imagePreview, setImagePreview] = useState(null);
  const [error, setError] = useState('');

  useEffect(() => {
    loadFabrics();
  }, []);

  const loadFabrics = async () => {
    try {
      setLoading(true);
      const response = await fetch(`${config.API_URL}/fabrics`);
      const data = await response.json();
      setFabrics(data.fabrics || []);
    } catch (error) {
      console.error('Failed to load fabrics:', error);
      setError('Failed to load fabrics');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const formDataToSend = new FormData();
      formDataToSend.append('name', formData.name);
      formDataToSend.append('description', formData.description);
      if (formData.main_image) {
        formDataToSend.append('main_image', formData.main_image);
      }

      const url = editingFabric 
        ? `${config.API_URL}/fabrics/${editingFabric.id}`
        : `${config.API_URL}/fabrics`;
      
      const method = editingFabric ? 'PUT' : 'POST';

      const response = await fetch(url, {
        method,
        body: formDataToSend
      });

      if (response.ok) {
        setShowModal(false);
        setFormData({ name: '', description: '', main_image: null });
        setImagePreview(null);
        setEditingFabric(null);
        loadFabrics();
      } else {
        setError('Failed to save fabric');
      }
    } catch (error) {
      console.error('Error saving fabric:', error);
      setError('Failed to save fabric');
    }
  };

  const handleDelete = async (id) => {
    if (window.confirm('Are you sure you want to delete this fabric?')) {
      try {
        const response = await fetch(`${config.API_URL}/fabrics/${id}`, {
          method: 'DELETE'
        });
        if (response.ok) {
          loadFabrics();
        } else {
          setError('Failed to delete fabric');
        }
      } catch (error) {
        console.error('Error deleting fabric:', error);
        setError('Failed to delete fabric');
      }
    }
  };

  const handleEdit = (fabric) => {
    setEditingFabric(fabric);
    setFormData({
      name: fabric.name,
      description: fabric.description,
      main_image: null
    });
    setImagePreview(fabric.main_image ? `${config.API_URL}${fabric.main_image}` : null);
    setShowModal(true);
  };

  const handleImageChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setFormData(prev => ({ ...prev, main_image: file }));
      const reader = new FileReader();
      reader.onload = (e) => setImagePreview(e.target.result);
      reader.readAsDataURL(file);
    }
  };

  const resetForm = () => {
    setFormData({ name: '', description: '', main_image: null });
    setImagePreview(null);
    setEditingFabric(null);
    setError('');
  };

  if (loading) {
    return (
      <Container fluid className="py-4">
        <div className="text-center">
          <Spinner animation="border" role="status">
            <span className="visually-hidden">Loading...</span>
          </Spinner>
        </div>
      </Container>
    );
  }

  return (
    <Container fluid className="py-4">
      <div className="d-flex justify-content-between align-items-center mb-4">
        <h2>Manage Fabrics</h2>
        <Button variant="primary" onClick={() => { resetForm(); setShowModal(true); }}>
          <Plus size={16} className="me-2" />
          Add New Fabric
        </Button>
      </div>

      {error && (
        <Alert variant="danger" onClose={() => setError('')} dismissible>
          {error}
        </Alert>
      )}

      {fabrics.length === 0 ? (
        <Alert variant="info">
          No fabrics found. Add your first fabric to get started.
        </Alert>
      ) : (
        <Table responsive striped hover>
          <thead>
            <tr>
              <th>Image</th>
              <th>Name</th>
              <th>Description</th>
              <th>Created</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {fabrics.map((fabric) => (
              <tr key={fabric.id}>
                <td>
                  {fabric.main_image && (
                    <Image 
                      src={(() => {
                        // Eğer path zaten /api ile başlıyorsa, sadece domain ekle
                        if (fabric.main_image.startsWith('/api/')) {
                          return `https://tekstas.com${fabric.main_image}`;
                        }
                        // Eğer path /uploads ile başlıyorsa, API_URL ile birleştir
                        else if (fabric.main_image.startsWith('/uploads/')) {
                          return `${config.API_URL}${fabric.main_image}`;
                        }
                        // Diğer durumlar için API_URL ile birleştir
                        else {
                          return `${config.API_URL}${fabric.main_image}`;
                        }
                      })()}
                      alt={fabric.name}
                      style={{ width: '50px', height: '50px', objectFit: 'cover' }}
                      onError={(e) => {
                        e.target.style.display = 'none';
                      }}
                    />
                  )}
                </td>
                <td>{fabric.name}</td>
                <td>{fabric.description}</td>
                <td>{new Date(fabric.created_at).toLocaleDateString()}</td>
                <td>
                  <div className="d-flex gap-2">
                    <Button
                      variant="outline-primary"
                      size="sm"
                      onClick={() => handleEdit(fabric)}
                    >
                      <Edit size={16} />
                    </Button>
                    <Button
                      variant="outline-danger"
                      size="sm"
                      onClick={() => handleDelete(fabric.id)}
                    >
                      <Trash2 size={16} />
                    </Button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </Table>
      )}

      <Modal show={showModal} onHide={() => { setShowModal(false); resetForm(); }} size="lg">
        <Modal.Header closeButton>
          <Modal.Title>
            {editingFabric ? 'Edit Fabric' : 'Add New Fabric'}
          </Modal.Title>
        </Modal.Header>
        <Form onSubmit={handleSubmit}>
          <Modal.Body>
            <Form.Group className="mb-3">
              <Form.Label>Fabric Name</Form.Label>
              <Form.Control
                type="text"
                value={formData.name}
                onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
                required
              />
            </Form.Group>
            
            <Form.Group className="mb-3">
              <Form.Label>Description</Form.Label>
              <Form.Control
                as="textarea"
                rows={3}
                value={formData.description}
                onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))}
                required
              />
            </Form.Group>
            
            <Form.Group className="mb-3">
              <Form.Label>Main Image</Form.Label>
              <Form.Control
                type="file"
                accept="image/*"
                onChange={handleImageChange}
                required={!editingFabric}
              />
              {imagePreview && (
                <div className="mt-2">
                  <Image 
                    src={imagePreview}
                    alt="Preview"
                    style={{ maxWidth: '200px', maxHeight: '200px' }}
                  />
                </div>
              )}
            </Form.Group>
          </Modal.Body>
          <Modal.Footer>
            <Button variant="secondary" onClick={() => { setShowModal(false); resetForm(); }}>
              Cancel
            </Button>
            <Button variant="primary" type="submit">
              {editingFabric ? 'Update' : 'Create'} Fabric
            </Button>
          </Modal.Footer>
        </Form>
      </Modal>
    </Container>
  );
};

export default FabricsManagePage; 